// hooks/useRelatedOffers.ts

import { useState, useEffect, useMemo } from 'react';
import { getRelatedOffers } from '@/helpers/related-offers';
import { browserClient as client } from '@/lib/apolloClient'; // same-origin proxy client
import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import { GET_ACTIVE_OFFERS } from '@/lib/queries/meetingPlacePageOfferQuery';
import { IMeetingPlacePageTenantShopItem } from '@/lib/fragments/meetingPlacePageTenantShopItem';
import { IMeetingPlacePageTenantFoodBevItem } from '@/lib/fragments/meetingPlacePageTenantFoodBevItem';
import { IMeetingPlacePageTenantEntertainmentItem } from '@/lib/fragments/meetingPlacePageTenantEntertainmentItem';
import { getTenantRelatedOffers } from '@/helpers/tenant-related-offers';

interface UseRelatedOffersProps {
  currentOffer?: IMeetingPlacePageOfferItem;
  currentTenant?:
    | IMeetingPlacePageTenantShopItem
    | IMeetingPlacePageTenantFoodBevItem
    | IMeetingPlacePageTenantEntertainmentItem;
  activeOfferIds: { sys: { id: string } }[];
  node_locale: string;
}

interface UseRelatedOffersReturn {
  relatedOffers?: IMeetingPlacePageOfferItem[];
  loading: boolean;
  error: Error | null;
}

export function useRelatedOffers({
  currentOffer,
  currentTenant,
  activeOfferIds,
  node_locale,
}: UseRelatedOffersProps): UseRelatedOffersReturn {
  const [allActiveOffers, setAllActiveOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const ids = activeOfferIds
      ?.map((offer) => offer?.sys?.id)
      .filter(
        (id): id is string => Boolean(id) && id !== currentOffer?.sys?.id,
      ); // exclude current

    if (!ids?.length) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Chunk into batches of 100 to respect Contentful's id_in limit
        const BATCH_SIZE = 100;
        const batches: string[][] = [];
        for (let i = 0; i < ids?.length; i += BATCH_SIZE) {
          batches.push(ids.slice(i, i + BATCH_SIZE));
        }

        const results = await Promise.all(
          batches.map((batchIds) =>
            client.query({
              query: GET_ACTIVE_OFFERS,
              variables: { ids: batchIds, language: node_locale },
              fetchPolicy: 'no-cache',
              errorPolicy: 'all',
            }),
          ),
        );

        const offers = results.flatMap(
          (result) =>
            (result?.data as any)?.meetingPlacePageOfferCollection?.items ?? [],
        );

        setAllActiveOffers(offers);
      } catch (err) {
        console.error('Error fetching related offers:', err);
        setError(
          err instanceof Error ? err : new Error('Failed to fetch offers'),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeOfferIds, currentOffer?.sys?.id, node_locale]);

  const relatedOffers = useMemo(() => {
    if (currentOffer) {
      return getRelatedOffers(currentOffer, allActiveOffers);
    }
    if (currentTenant) {
      return getTenantRelatedOffers(currentTenant, allActiveOffers);
    }
  }, [currentOffer, currentTenant, allActiveOffers]);

  return { relatedOffers, loading, error };
}
